import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAdminAppearance } from '../../contexts/AdminAppearanceContext';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsDashboard from './AnalyticsDashboard';
import '../../styles/global.css';

interface LineStats {
  line_id: number;
  line_name: string;
  is_active: boolean;
  items_nie_rozpoczete: number;
  items_in_progress: number;
  items_completed: number;
  items_anulowane: number;
  items_problem_bb: number;
  items_problem_awaria: number;
  items_niepelna_produkcja: number;
  notes_count: number;
}

interface DashboardStats {
  total_lines: number;
  active_lines: number;
  total_assignments: number;
  pending_assignments: number;
  in_progress_assignments: number;
  completed_assignments: number;
  lines_stats: LineStats[];
  available_weeks: { week: string; is_closed: boolean }[];
}

interface ProductionLine {
  id: number;
  name: string;
}

interface LineStatus {
  production_line: ProductionLine;
  status: {
    pending: number;
    in_progress: number;
    completed: number;
  };
  assignments: any[];
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { columnWidthLeft, columnWidthRight } = useAdminAppearance();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [lines, setLines] = useState<ProductionLine[]>([]);
  const [selectedLine, setSelectedLine] = useState('');
  const [lineStatus, setLineStatus] = useState<LineStatus | null>(null);
  const [filterWeek, setFilterWeek] = useState('');
  const [showClosedWeeks, setShowClosedWeeks] = useState(false);

  useEffect(() => {
    fetchLines();
  }, []);

  useEffect(() => {
    const params = filterWeek ? `?week=${encodeURIComponent(filterWeek)}` : '';
    api.get(`/admin/dashboard${params}`)
      .then((res) => setStats(res.data))
      .catch((err) => console.error('Error fetching stats:', err));
  }, [filterWeek]);

  useEffect(() => {
    if (selectedLine) {
      fetchLineStatus();
    }
  }, [selectedLine, filterWeek]);

  const fetchStats = async () => {
    try {
      const params = filterWeek ? `?week=${encodeURIComponent(filterWeek)}` : '';
      const response = await api.get(`/admin/dashboard${params}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchLines = async () => {
    try {
      const response = await api.get('/production-lines');
      setLines(response.data);
    } catch (error) {
      console.error('Error fetching lines:', error);
    }
  };

  const fetchLineStatus = async () => {
    try {
      const url = `/admin/production-lines/${selectedLine}/status${filterWeek ? `?week=${filterWeek}` : ''}`;
      const response = await api.get(url);
      setLineStatus(response.data);
    } catch (error) {
      console.error('Error fetching line status:', error);
    }
  };

  const getProgressPercentage = (status: { pending: number; in_progress: number; completed: number }) => {
    const total = status.pending + status.in_progress + status.completed;
    if (total === 0) return 0;
    return Math.round((status.completed / total) * 100);
  };

  return (
    <div>
      <div className="page-header">
        <h1>📊 {t('admin.dashboard')}</h1>
      </div>

      {stats && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <h3>{t('admin.productionLines')}</h3>
              <p>{t('admin.total')}: {stats.total_lines}</p>
              <p>{t('admin.active')}: {stats.active_lines}</p>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)' }}>
              <h3>{t('admin.assignments')}</h3>
              <p>{t('admin.total')}: {stats.total_assignments}</p>
              <p>{t('admin.pending')}: {stats.pending_assignments}</p>
              <p>{t('admin.inProgress')}: {stats.in_progress_assignments}</p>
              <p>{t('admin.completed')}: {stats.completed_assignments}</p>
            </div>
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' }}>
              <h3>{t('admin.completionRate')}</h3>
              <p>
                {stats.total_assignments > 0
                  ? Math.round((stats.completed_assignments / stats.total_assignments) * 100)
                  : 0}%
              </p>
            </div>
            {stats.lines_stats && stats.lines_stats.length > 0 && (
              <div className="stat-card" style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
                <h3>Ze wszystkich linii (szt.)</h3>
                <p>{t('admin.notStarted')}: {stats.lines_stats.reduce((s, l) => s + (l.items_nie_rozpoczete ?? 0), 0)}</p>
                <p>{t('admin.inProgress')}: {stats.lines_stats.reduce((s, l) => s + (l.items_in_progress ?? 0), 0)}</p>
                <p>{t('admin.completed')}: {stats.lines_stats.reduce((s, l) => s + (l.items_completed ?? 0), 0)}</p>
                <p>{t('admin.cancelled')}: {stats.lines_stats.reduce((s, l) => s + (l.items_anulowane ?? 0), 0)}</p>
                <p>Problem BB: {stats.lines_stats.reduce((s, l) => s + (l.items_problem_bb ?? 0), 0)}</p>
                <p>Problem Awaria: {stats.lines_stats.reduce((s, l) => s + (l.items_problem_awaria ?? 0), 0)}</p>
                <p>Niepełna prod.: {stats.lines_stats.reduce((s, l) => s + (l.items_niepelna_produkcja ?? 0), 0)}</p>
              </div>
            )}
          </div>

          <div className="card" style={{ marginTop: '20px', marginBottom: '20px' }}>
            <h2 style={{ marginTop: 0, marginBottom: '24px', color: '#2d3748' }}>{t('admin.lineStatus')}</h2>
            <div className="filter-bar">
              <label>
                {t('admin.selectLine')}:
                <select
                  value={selectedLine}
                  onChange={(e) => setSelectedLine(e.target.value)}
                  className="input"
                  style={{ width: '200px', marginLeft: '8px' }}
                >
                  <option value="">{t('admin.selectLine')}</option>
                  {lines.map((line) => (
                    <option key={line.id} value={line.id}>
                      {line.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {stats?.available_weeks && stats.available_weeks.length > 0 && (
              <div style={{ marginTop: '12px', marginBottom: '4px' }}>
                <label style={{ fontWeight: '500', color: '#4a5568', fontSize: '15px', display: 'block', marginBottom: '8px' }}>
                  📅 {t('admin.filterByWeek')}:
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setFilterWeek('')}
                    style={{ padding: '8px 14px', border: filterWeek === '' ? '3px solid #667eea' : '2px solid #e1e8ed', borderRadius: '8px', fontSize: '14px', fontWeight: filterWeek === '' ? 600 : 400, backgroundColor: filterWeek === '' ? '#e7f3ff' : 'white', color: filterWeek === '' ? '#667eea' : '#4a5568', cursor: 'pointer' }}
                  >
                    {t('adminSearch.allWeeks')}
                  </button>
                  {stats.available_weeks
                    .filter(w => showClosedWeeks || !w.is_closed)
                    .map(({ week, is_closed }) => (
                      <button
                        key={week}
                        type="button"
                        onClick={() => setFilterWeek(week)}
                        style={{ padding: '8px 14px', border: filterWeek === week ? '3px solid #667eea' : '2px solid #e1e8ed', borderRadius: '8px', fontSize: '14px', fontWeight: filterWeek === week ? 600 : 400, backgroundColor: filterWeek === week ? '#e7f3ff' : is_closed ? '#f7fafc' : 'white', color: filterWeek === week ? '#667eea' : is_closed ? '#a0aec0' : '#4a5568', cursor: 'pointer', textDecoration: is_closed ? 'line-through' : 'none', opacity: is_closed ? 0.7 : 1 }}
                      >
                        {week}
                      </button>
                    ))}
                  {stats.available_weeks.some(w => w.is_closed) && (
                    <button
                      type="button"
                      onClick={() => setShowClosedWeeks(v => !v)}
                      style={{ fontSize: '12px', padding: '0', border: 'none', background: 'none', color: '#a0aec0', cursor: 'pointer', textDecoration: 'underline', marginLeft: '4px' }}
                    >
                      {showClosedWeeks ? t('admin.hideClosedWeeks') : t('admin.showClosedWeeks')}
                    </button>
                  )}
                </div>
              </div>
            )}

            {lineStatus && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3 style={{ margin: 0, color: '#667eea' }}>{lineStatus.production_line.name}</h3>
                  <button
                    onClick={() => navigate(`/admin/production-lines/${selectedLine}/view${filterWeek ? '?week=' + encodeURIComponent(filterWeek) : ''}`)}
                    className="btn btn-primary"
                  >
                    {t('admin.detailedView')}
                  </button>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <p><strong>{t('admin.pending')}:</strong> {lineStatus.status.pending}</p>
                  <p><strong>{t('admin.inProgress')}:</strong> {lineStatus.status.in_progress}</p>
                  <p><strong>{t('admin.completed')}:</strong> {lineStatus.status.completed}</p>
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ width: '100%', backgroundColor: '#e9ecef', borderRadius: '8px', height: '32px', overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${getProgressPercentage(lineStatus.status)}%`,
                          background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: '600',
                          transition: 'width 0.5s ease',
                        }}
                      >
                        {getProgressPercentage(lineStatus.status)}%
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 style={{ marginBottom: '12px', color: '#4a5568' }}>{t('admin.assignments')}:</h4>
                  {lineStatus.assignments.map((assignment) => (
                    <div key={assignment.id} className="card" style={{ marginBottom: '12px', padding: '16px' }}>
                      <p style={{ margin: 0 }}>
                        <strong>{t('table.week')}:</strong> {assignment.week} |
                        <strong> {t('table.status')}:</strong> {assignment.status === 'pending' ? t('admin.pending') : assignment.status === 'in_progress' ? t('admin.inProgress') : t('admin.completed')} |
                        <strong> {t('table.assignedUsers')}:</strong> {assignment.user?.name || t('admin.unassigned')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Statistics table per production line – wartości to suma sztuk nominalnych (do_produkcji) na tydzień */}
          <div className="card" style={{ marginTop: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, color: '#2d3748' }}>📊 Statystyki produktów per linia produkcyjna (szt.)</h2>
              {filterWeek && (
                <span style={{ fontSize: '14px', color: '#667eea', fontWeight: 500 }}>Tydzień: {filterWeek}</span>
              )}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: 'max-content',
                  minWidth: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  tableLayout: 'fixed',
                }}
              >
                <colgroup>
                  <col style={{ width: `${columnWidthLeft}px`, minWidth: `${columnWidthLeft}px` }} />
                  <col style={{ width: `${columnWidthRight}px`, minWidth: `${columnWidthRight}px` }} />
                  <col style={{ width: `${columnWidthRight}px`, minWidth: `${columnWidthRight}px` }} />
                  <col style={{ width: `${columnWidthRight}px`, minWidth: `${columnWidthRight}px` }} />
                  <col style={{ width: `${columnWidthRight}px`, minWidth: `${columnWidthRight}px` }} />
                  <col style={{ width: `${columnWidthRight}px`, minWidth: `${columnWidthRight}px` }} />
                  <col style={{ width: `${columnWidthRight}px`, minWidth: `${columnWidthRight}px` }} />
                  <col style={{ width: `${columnWidthRight}px`, minWidth: `${columnWidthRight}px` }} />
                  <col style={{ width: `${columnWidthRight}px`, minWidth: `${columnWidthRight}px` }} />
                  <col style={{ width: `${columnWidthRight}px`, minWidth: `${columnWidthRight}px` }} />
                </colgroup>
                <thead>
                  <tr style={{ backgroundColor: '#667eea', color: 'white' }}>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #dee2e6', fontSize: '10px', overflow: 'hidden', textOverflow: 'ellipsis' }}>Linia Produkcyjna</th>
                    <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6', fontSize: '10px', backgroundColor: '#94a3b8', color: 'white', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>Nie rozpoczęte (szt.)</th>
                    <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6', fontSize: '10px', backgroundColor: '#f59e0b', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>W trakcie (szt.)</th>
                    <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6', fontSize: '10px', backgroundColor: '#10b981', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>Zakończone (szt.)</th>
                    <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6', fontSize: '10px', backgroundColor: '#6c757d', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>Anulowane (szt.)</th>
                    <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6', fontSize: '10px', backgroundColor: '#dc3545', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>Problem BB (szt.)</th>
                    <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6', fontSize: '10px', backgroundColor: '#dc3545', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>Problem Awaria (szt.)</th>
                    <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6', fontSize: '10px', backgroundColor: '#dc3545', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>Niepełna prod. (szt.)</th>
                    <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6', fontSize: '10px', backgroundColor: '#667eea', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>{t('table.notes')}</th>
                    <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #dee2e6', fontSize: '10px', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.lines_stats && stats.lines_stats.map((lineStat) => (
                    <tr
                      key={lineStat.line_id}
                      style={{
                        backgroundColor: lineStat.is_active ? 'white' : '#f8f9fa',
                        borderBottom: '1px solid #dee2e6'
                      }}
                    >
                      <td style={{ padding: '12px', border: '1px solid #dee2e6', fontWeight: '600', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {lineStat.line_name}
                        {!lineStat.is_active && (
                          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>(Nieaktywna)</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', backgroundColor: lineStat.items_nie_rozpoczete > 0 ? '#e2e8f0' : 'transparent', fontWeight: lineStat.items_nie_rozpoczete > 0 ? 'bold' : 'normal', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>
                        {lineStat.items_nie_rozpoczete}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', backgroundColor: lineStat.items_in_progress > 0 ? '#fff3cd' : 'transparent', fontWeight: lineStat.items_in_progress > 0 ? 'bold' : 'normal', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>
                        {lineStat.items_in_progress}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', backgroundColor: lineStat.items_completed > 0 ? '#d1fae5' : 'transparent', fontWeight: lineStat.items_completed > 0 ? 'bold' : 'normal', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>
                        {lineStat.items_completed}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', backgroundColor: lineStat.items_anulowane > 0 ? '#e9ecef' : 'transparent', fontWeight: lineStat.items_anulowane > 0 ? 'bold' : 'normal', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>
                        {lineStat.items_anulowane}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', backgroundColor: lineStat.items_problem_bb > 0 ? '#f8d7da' : 'transparent', fontWeight: lineStat.items_problem_bb > 0 ? 'bold' : 'normal', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>
                        {lineStat.items_problem_bb}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', backgroundColor: lineStat.items_problem_awaria > 0 ? '#f8d7da' : 'transparent', fontWeight: lineStat.items_problem_awaria > 0 ? 'bold' : 'normal', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>
                        {lineStat.items_problem_awaria}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', backgroundColor: lineStat.items_niepelna_produkcja > 0 ? '#fff3cd' : 'transparent', fontWeight: lineStat.items_niepelna_produkcja > 0 ? 'bold' : 'normal', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>
                        {lineStat.items_niepelna_produkcja}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', backgroundColor: lineStat.notes_count > 0 ? '#e7f3ff' : 'transparent', fontWeight: lineStat.notes_count > 0 ? 'bold' : 'normal', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>
                        {lineStat.notes_count}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>
                        <button
                          onClick={() => navigate(`/admin/production-lines/${lineStat.line_id}/view${filterWeek ? '?week=' + encodeURIComponent(filterWeek) : ''}`)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                          }}
                        >
                          Podgląd
                        </button>
                      </td>
                    </tr>
                  ))}
                  {stats.lines_stats && stats.lines_stats.length > 0 && (() => {
                    const totals = stats.lines_stats.reduce((acc: any, l: any) => ({
                      items_nie_rozpoczete: acc.items_nie_rozpoczete + (l.items_nie_rozpoczete || 0),
                      items_in_progress: acc.items_in_progress + (l.items_in_progress || 0),
                      items_completed: acc.items_completed + (l.items_completed || 0),
                      items_anulowane: acc.items_anulowane + (l.items_anulowane || 0),
                      items_problem_bb: acc.items_problem_bb + (l.items_problem_bb || 0),
                      items_problem_awaria: acc.items_problem_awaria + (l.items_problem_awaria || 0),
                      items_niepelna_produkcja: acc.items_niepelna_produkcja + (l.items_niepelna_produkcja || 0),
                      notes_count: acc.notes_count + (l.notes_count || 0),
                    }), { items_nie_rozpoczete: 0, items_in_progress: 0, items_completed: 0, items_anulowane: 0, items_problem_bb: 0, items_problem_awaria: 0, items_niepelna_produkcja: 0, notes_count: 0 });
                    return (
                      <tr style={{ backgroundColor: '#eef2ff', fontWeight: 700, borderTop: '2px solid #667eea' }}>
                        <td style={{ padding: '12px', border: '1px solid #dee2e6', width: columnWidthLeft, minWidth: columnWidthLeft, maxWidth: columnWidthLeft }}>RAZEM</td>
                        <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>{totals.items_nie_rozpoczete}</td>
                        <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>{totals.items_in_progress}</td>
                        <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>{totals.items_completed}</td>
                        <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>{totals.items_anulowane}</td>
                        <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>{totals.items_problem_bb}</td>
                        <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>{totals.items_problem_awaria}</td>
                        <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>{totals.items_niepelna_produkcja}</td>
                        <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}>{totals.notes_count}</td>
                        <td style={{ padding: '12px', border: '1px solid #dee2e6', textAlign: 'center', width: columnWidthRight, minWidth: columnWidthRight, maxWidth: columnWidthRight }}></td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <AnalyticsDashboard />
    </div>
  );
};

export default AdminDashboard;

